import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild
} from '@angular/core';
import { NgClass } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { PorIconDirective } from '@por/shared/ui/icon-lib';

import { BehaviorSubject, startWith, Subscription } from 'rxjs';
import { ApxControlChange } from '../models/change.model';
import { ApxControlDirective } from '../form/control.directive';
import { ApxInputDirective } from '../input/input.directive';
import { ComponentSubscription } from '../utils/decorators/component-subscription';
import { ApxDropdownOption } from './dropdown.model';
import { ApxDropdownOptionComponent } from './dropdown-option/dropdown-option.component';
import { ApxDropdownOptionItemComponent } from './dropdown-option-item/dropdown-option-item.component';
import { ApxOverlayModule } from '../utils/overlay/overlay.module';
import { ApxButtonModule } from '../button/button.component.module';

let nextUniqueId = 0;

@Component({
    selector: 'apx-dropdown',
    standalone: true,
    imports: [ReactiveFormsModule, ScrollingModule, ApxInputDirective, ApxDropdownOptionItemComponent, ApxOverlayModule, ApxButtonModule, NgClass, PorIconDirective],
    templateUrl: './dropdown.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: ApxControlDirective,
            useExisting: forwardRef(() => ApxDropdownComponent)
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ApxDropdownComponent),
            multi: true
        }
    ]
})
export class ApxDropdownComponent extends ApxControlDirective implements OnDestroy, AfterContentInit, AfterViewInit, ControlValueAccessor {
    @ComponentSubscription subs: Subscription[] = [];
    @Input() maxDisplayItems = 5;
    @Input() enableSearch = false;
    @Input() disableClear = false;
    @Input() noResultsLabel = 'No results found';
    @Input() set disabled(disabled: boolean) {
        this._disabled = disabled;
        disabled ? this.inputControl.disable() : this.inputControl.enable();
    }
    get disabled() {
        return this._disabled;
    }
    @Output() readonly valueChanged = new EventEmitter<ApxControlChange>();
    @ViewChild('overlayOrigin', { static: false }) overlayOrigin!: ElementRef;
    @ViewChild(ApxInputDirective)
    internalInput!: ApxInputDirective;
    @ViewChild('input')
    inputElement!: ElementRef;
    @ViewChild(CdkVirtualScrollViewport)
    scrollViewPort!: CdkVirtualScrollViewport;
    @ContentChildren(ApxDropdownOptionComponent, { descendants: true })
    apxOptionComponentList!: QueryList<ApxDropdownOptionComponent>;
    @HostBinding('class.w-full')
    fullWidthClass = true;
    private _currentValue: string | number | null = null;
    private _currentOption!: ApxDropdownOption | null;
    private readonly modelChangesSub = new BehaviorSubject<{
        value: string | null;
        skipEmit?: boolean;
    }>({ value: null });
    private readonly pendingInvalidSub = new BehaviorSubject(false);
    private readonly pendingInvalid$ = this.pendingInvalidSub.asObservable();
    private readonly modelChanges$ = this.modelChangesSub.asObservable();
    private readonly filterValueSub = new BehaviorSubject<string | null>('');
    private readonly filterValue$ = this.filterValueSub.asObservable();
    private _disabled!: boolean;
    private _menuOpen = false;
    private _options: ApxDropdownOption[] = [];
    private _filteredOptions: ApxDropdownOption[] = [];

    public changed!: (value: unknown) => void;
    public touched!: () => void;
    readonly inputId = `apx-dropdown-${nextUniqueId++}`;
    readonly closeBtnId = `${this.inputId}-close`;
    readonly itemSize = 35;

    overlayWidth!: number;
    inputControl = new FormControl<string>('');
    currentlyActiveIndex: number | null = null;
    scrollViewportHeight = this.calcScrollViewport();

    get options(): ApxDropdownOption[] {
        return this._options;
    }

    set options(opts: ApxDropdownOption[]) {
        this._options = opts;
        this.scrollViewportHeight = this.calcScrollViewport();
    }

    get filteredOptions(): ApxDropdownOption[] {
        return this._filteredOptions;
    }

    set filteredOptions(opts: ApxDropdownOption[]) {
        this._filteredOptions = opts;
        this.scrollViewportHeight = this.calcScrollViewport();
    }

    get currentValue(): string | number | null {
        return this._currentValue;
    }

    set currentValue(value: string | number | null) {
        this._currentValue = value;
    }

    get currentOption(): ApxDropdownOption | null {
        return this._currentOption;
    }

    set currentOption(option: ApxDropdownOption | null) {
        this._currentOption = option;
    }

    get displayOptions(): ApxDropdownOption[] {
        return this.enableSearch ? this.filteredOptions : this.options;
    }

    get menuOpen(): boolean {
        return this._menuOpen;
    }

    set menuOpen(open: boolean) {
        this._menuOpen = open;
        if (!open) {
            this.filterValueSub.next(null);
            this.onMenuClose();
        }
    }

    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
    ngOnDestroy(): void {}

    ngAfterViewInit(): void {
        // Listen for pending invalid state being set
        this.subs.push(
            this.pendingInvalid$.subscribe(isInvalid => {
                if (this.internalInput) {
                    this.internalInput.isInvalid = isInvalid;
                }
            })
        );

        // Set the overlay width based on the width of the input element
        this.overlayWidth = (this.overlayOrigin.nativeElement as HTMLDivElement).getBoundingClientRect().width;
    }

    ngAfterContentInit(): void {
        // Listen for changes in the projected options and update the list internally
        this.subs.push(
            this.apxOptionComponentList.changes.pipe(startWith(true)).subscribe(() => {
                this.options = this.apxOptionComponentList.toArray().map(optionComp => {
                    return {
                        label: optionComp.label,
                        value: optionComp.value
                    };
                });
                if (this.enableSearch) {
                    this.filteredOptions = this.options;
                }
                this.cdr.markForCheck();
            })
        );

        // Listen for changes to the value from the reactive form OR manual setting of value and set option accordingly
        this.subs.push(
            this.modelChanges$.subscribe(({ value, skipEmit }) => {
                this.updateCurrentValue(value, skipEmit);
                this.cdr.markForCheck();
            })
        );

        // Listen for changes in the internal input to update filtering (search only)
        this.subs.push(
            this.inputControl.valueChanges.subscribe(value => {
                this.filterValueSub.next(value);
            })
        );

        // Listen for filter value changes (search only)
        if (this.enableSearch) {
            this.subs.push(
                this.filterValue$.subscribe(filterVal => {
                    if (filterVal) {
                        this.filteredOptions = this.options.filter(option => {
                            return option.label.toLowerCase().includes(filterVal.toLowerCase());
                        });
                    } else {
                        this.filteredOptions = this.options;
                    }
                    this.currentlyActiveIndex = null;
                    this.cdr.detectChanges();
                })
            );
        }
    }

    /**
     * This setValue is called when a value is manually passed into the component
     * rather than through a reactive form
     * @param value
     */
    override writeManualValue(value: string): void {
        this.modelChangesSub.next({ value, skipEmit: true });
    }

    writeValue(value: string): void {
        this.modelChangesSub.next({ value, skipEmit: true });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOnChange(fn: any): void {
        this.changed = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOnTouched(fn: any): void {
        this.touched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    /**
     * Handler selection events originating from the items
     * @param value
     */
    selectItem(value: string | null): void {
        this.modelChangesSub.next({ value });
        this.menuOpen = false;
    }

    /**
     * Clear the selection when the clear button is interacted with
     */
    clearSelection() {
        this.currentOption = null;
        this.updateCurrentValue(null);

        // If menu is open, focus the input for textual input
        if (this.menuOpen) {
            this.inputElement.nativeElement.focus();
        }
    }

    /**
     * Handlers for keyboard accessibility. The component supports focusing using arrow keys and
     * selection using the enter key. The enter key will also enable showing / hiding the menu
     * @param event
     */
    handleKeyDown(event: KeyboardEvent): void {
        /* In the event that a user has reverse-tabbed by holding shift, close the menu */
        if (event.key === 'Tab' || event.key === 'Escape') {
            this.menuOpen = false;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.focusDropdownItem('backward');
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.focusDropdownItem('forward');
        }

        if (event.key === 'Enter') {
            if (this.currentlyActiveIndex !== null) {
                // If there's a focused option, set this as the current value and close the menu
                this.updateCurrentValue(this.displayOptions[this.currentlyActiveIndex].value);
                this.menuOpen = false;
            } else {
                // otherwise, toggle the menu, it it's already open then close it
                this.menuOpen = this.menuOpen ? false : true;
            }
            event.preventDefault();
            this.cdr.markForCheck();
        }
    }

    override setInvalidStyling(isInvalid: boolean): void {
        this.pendingInvalidSub.next(isInvalid);
    }

    /**
     * Update the current internal value and emit the change, unless skipEmit is specified
     * @param value
     * @param skipEmit
     */
    private updateCurrentValue(value: string | null, skipEmit = false): void {
        if (value !== this._currentValue) {
            this._currentValue = value;
            this.updateOption(value);
            if (!skipEmit) {
                this.outputChangedValue(value);
            }
        }
    }

    /**
     * Update the internal value for the current option
     * @param value
     */
    private updateOption(value: string | null): void {
        const matchingOption = value ? this.findOption(value) : null;
        this.currentOption = matchingOption || null;
        this.inputControl.setValue(matchingOption ? matchingOption.label : null);
        this.cdr.markForCheck();
    }

    /**
     * Update the Reactive model via the ControlValueAccessor 'registerOnChange'
     * provided function and also emit the change for use cases where the
     * consumer is using it in a standalone manner
     * @param value
     */
    private outputChangedValue(value: string | number | null): void {
        this.valueChanged.emit({ value: value as string });
        if (this.changed) {
            this.changed(value);
        }
    }

    /**
     * Perform cleanup actions on menu closure
     */
    private onMenuClose(): void {
        // If search has been enabled, tidy up the entered text by selecting an option if applicable
        if (this.enableSearch) {
            if (this.currentOption) {
                this.inputControl.setValue(this.currentOption.label, {
                    emitEvent: false
                });
            } else {
                this.inputControl.setValue(null);
            }
        }

        this.currentlyActiveIndex = null;
    }

    /**
     * Find the respective option by looking up using the value
     * @param value
     * @returns ApxDropdownOption or undefined if match not found
     */
    private findOption(value: string): ApxDropdownOption | undefined {
        if (this.apxOptionComponentList?.length) {
            return this.apxOptionComponentList.toArray().find(option => {
                return option.value.toLowerCase() === value.toLowerCase();
            });
        } else {
            return undefined;
        }
    }

    /**
     * Focus the next dropdown option in the sequence based on direction for keyboard accessibility
     * @param direction
     */
    private focusDropdownItem(direction: 'forward' | 'backward'): void {
        if (this.menuOpen) {
            const curIdx = this.currentlyActiveIndex;
            const displayedOptions = this.displayOptions;
            if (direction === 'forward') {
                const startIndex = curIdx !== null ? curIdx + 1 : 0;
                const incrementAmt = [...displayedOptions.slice(startIndex, this.options.length)].findIndex(opt => opt) + 1;

                if (incrementAmt) {
                    if (typeof curIdx === 'number') {
                        this.currentlyActiveIndex = curIdx + incrementAmt;
                    } else {
                        this.currentlyActiveIndex = -1 + incrementAmt;
                    }
                }
            } else {
                const decrementAmt = curIdx === null ? 0 : [...displayedOptions.slice(0, curIdx)].reverse().findIndex(opt => opt) + 1;

                if (curIdx !== null) {
                    this.currentlyActiveIndex = decrementAmt >= 0 ? curIdx - decrementAmt : curIdx;
                }
            }

            this.scrollViewPort.scrollToIndex(this.currentlyActiveIndex ?? 0, 'auto');
        }
    }

    /**
     * Calculate the height for the scrolling viewport;
     * @returns the height that the viewport for the scrolling container should be.
     */
    private calcScrollViewport(): number {
        return (this.scrollViewportHeight = this.displayOptions.length > this.maxDisplayItems ? this.maxDisplayItems * this.itemSize : this.displayOptions.length * this.itemSize);
    }
}
